import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useForm } from "react-hook-form";

import FormInput from "@/components/form-fields/form-input";
import FormDialog from "@/components/reusables/FormDialog";
import InputField from "@/components/reusables/custom-form-fields/InputField";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import * as Table from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { AdjustmentType } from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";
import {
  useGetSingleProductQuery,
  useUpdateProductQuantityMutation,
} from "@/redux/api/toolsQuery";
import {
  TAdjustProductQuantity,
  adjustProductQuantitySchema,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

const ItemTableHeader = (): JSX.Element => {
  const { theme: skin } = useTheme();

  const commonClasses = cn(
    "text-primary uppercase  ",
    skin === "dark" && "text-white",
    skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
  );

  return (
    <Table.TableHeader
      className={cn(skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight")}
    >
      <Table.TableRow>
        <Table.TableHead
          className={cn(
            commonClasses,
            "border-r border-r-white w-[300px] pl-8",
          )}
        >
          PRODUCT NAME
        </Table.TableHead>
        <Table.TableHead className={commonClasses}>Quantity</Table.TableHead>
      </Table.TableRow>
    </Table.TableHeader>
  );
};

interface AdjustQuantityModalProps {
  isOpen: boolean;
  toggleModal: () => void;
}

const AdjustQuantityModal = ({
  isOpen,
  toggleModal,
}: AdjustQuantityModalProps): JSX.Element => {
  const { id: productId, reset: resetProductId } = useQueryID(
    ToolsKeys.PRODUCT_ASSIGNMENT_ID,
  );
  const [updateProductQuantity, { isLoading }] =
    useUpdateProductQuantityMutation();
  const { data, isLoading: isProductFetching } = useGetSingleProductQuery(
    {
      id: productId,
    },
    {
      skip: !productId || !isOpen,
    },
  );
  const singleProduct = data?.data;

  const form = useForm<TAdjustProductQuantity>({
    resolver: zodResolver(adjustProductQuantitySchema),
    ...(singleProduct && {
      values: {
        quantity: 0,
        existingQuantity: +singleProduct?.quantity,
        action: AdjustmentType.ADD,
      },
    }),
    defaultValues: {
      quantity: 0,
      action: AdjustmentType.ADD,
    },
  });

  const handleExit = (): void => {
    resetProductId();
    toggleModal();
    form.reset();
  };

  const onSubmit = async (data: TAdjustProductQuantity): Promise<void> => {
    const response = await updateProductQuantity({
      data,
      productId,
    });

    validateResponse({
      response,
      onSuccess: () => {
        handleExit();
        customToast.success(
          ToolsConstants.FORM.PRODUCT.PRODUCT_QUANTITY_ADJUSTED,
        );
      },
    });
  };

  const commonTabTriggerClasses =
    "data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:border-b-2 rounded-none border-b-2 border-transparent";

  return (
    <FormDialog
      isOpen={isOpen}
      title={ToolsConstants.FORM.PRODUCT.ADJUST_QUANTITY}
      toggleModal={handleExit}
      isLoading={isProductFetching}
      isFormDirty={Object.keys(form.formState.dirtyFields).length > 0}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
          <Tabs defaultValue={AdjustmentType.ADD} className=''>
            <TabsList className=' space-x-7 ml-4'>
              <TabsTrigger
                value='ADD'
                onClick={() =>
                  form.setValue("action", AdjustmentType.ADD, {
                    shouldValidate: true,
                  })
                }
                className={cn(commonTabTriggerClasses)}
              >
                {ToolsConstants.FORM.PRODUCT.ADD_QTY}
              </TabsTrigger>
              <TabsTrigger
                value='REDUCE'
                onClick={() =>
                  form.setValue("action", AdjustmentType.REDUCE, {
                    shouldValidate: true,
                  })
                }
                className={cn(commonTabTriggerClasses)}
              >
                {ToolsConstants.FORM.PRODUCT.REDUCE_QTY}
              </TabsTrigger>
            </TabsList>
            <TabsContent value={AdjustmentType.ADD}>
              <div className='rounded-md border overflow-clip'>
                <Table.Table>
                  <ItemTableHeader />
                  <Table.TableBody>
                    <Table.TableRow>
                      <Table.TableCell className='p-4 border-r border-r-gray-200 pl-8'>
                        {singleProduct?.name}
                      </Table.TableCell>
                      <Table.TableCell>
                        <FormInput
                          name='quantity'
                          form={form}
                          input={{
                            type: "number",
                            placeholder: "0",
                            icon: {
                              icon: ArrowUp,
                              className: "!text-primary opacity-1",
                              position: "right",
                            },
                          }}
                        />
                      </Table.TableCell>
                    </Table.TableRow>
                  </Table.TableBody>
                </Table.Table>
              </div>
            </TabsContent>
            <TabsContent value={AdjustmentType.REDUCE}>
              <div className='rounded-md border overflow-clip'>
                <Table.Table>
                  <ItemTableHeader />
                  <Table.TableBody>
                    <Table.TableRow>
                      <Table.TableCell className='p-4 border-r border-r-gray-200 pl-8'>
                        {singleProduct?.name}
                      </Table.TableCell>
                      <Table.TableCell>
                        <FormInput
                          name='quantity'
                          form={form}
                          input={{
                            placeholder: "0",
                            type: "number",
                            icon: {
                              icon: ArrowDown,
                              className: "text-red-500 opacity-1",
                              position: "right",
                            },
                          }}
                        />
                      </Table.TableCell>
                    </Table.TableRow>
                  </Table.TableBody>
                </Table.Table>
              </div>
            </TabsContent>
          </Tabs>
          <FormInput
            name='remarks'
            form={form}
            render={(field) => (
              <InputField
                label='Remarks'
                type='textarea'
                rows={2}
                placeholder='Enter remarks'
                {...field}
              />
            )}
          />
          <div className='flex justify-between '>
            <Button
              onClick={() => form.reset()}
              variant={"ghost"}
              disabled={isLoading}
            >
              {Button_Names.Discard_Change}
            </Button>
            <Button
              type='submit'
              className='w-[300px]'
              isLoading={isLoading}
              disabled={!form.formState.isDirty || isLoading}
            >
              {Button_Names.Save_Changes}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default AdjustQuantityModal;
